import { RolesPermission } from "api/types";
import { CSVFrozenHeaderMapping } from "csv/mappings";
import { observer } from "mobx-react-lite";
import React, { useMemo, useState } from "react";
import { APIOutputSheetFileType } from "../../api/outputSheets";
import GenerateOutputsButton from "../../components/GenerateOutputsButton";
import Loading from "../../components/Loading";
import {
    getFrozenMappingCostingOutput,
    mappingCostSheet,
    mappingRawMaterialSummary,
} from "../../csv/mappings/output";
import { useStore } from "../../store";
import OutputSheet from "../../store/models/OutputSheet";
import WorkArea from "../WorkArea";
import { useProject } from "../Workspace/hooks";
import { TabList } from "../Workspace/types";
import FormulationsCpl from "./FormulationsCpl";
import FormulationSelectionsTab from "./FormulationSelectionsTab";
import { schemaCostingSheet, schemaRawMaterialSummary } from "./schema";
import SourcingSelectionsTab from "./SourcingSelectionsTab";
// import FormulationSelectionsTab from "./FormulationSelectionsTab";
// import SourcingSelectionsTab from "./SourcingSelectionsTab";

type Props = {};

export const outputSectionTabKeys = {
  costing_sheet: "costingSheet",
  formulation_cpl: "formulationsCpl",
  formulation_selection: "formulationSelections",
  sourcing_selection: "sourcingSelections",
  rm_summary: "rawMaterialSummary",
};

const OutputSection: React.FC<Props> = () => {
  const store = useStore();
  const project = useProject();
  const { current } = store.auth;

  const [frozenCosting, setFrozenCosting] = useState<CSVFrozenHeaderMapping>(
    getFrozenMappingCostingOutput(true)
  );

  const sheets = project
    ? store.outputSheets.ofProject(project.id)
    : new Map<APIOutputSheetFileType, OutputSheet[]>();

  // const costingSheetEditable =
  //   !project?.isLocked &&
  //   !!current &&
  //   (!!current.isAdmin || !!project?.adminUsers?.find((id) => current.id));

  const havePermissions = (subSection: keyof typeof outputSectionTabKeys) => {
    return !!(store.auth.getPermissions('output', subSection)?.toLowerCase() === 'edit' && 
    current && project?.adminUsers.includes(current.id) && !project?.isLocked);
  }

  const costingSheetEditable =
    !project?.isLocked &&
    !!current &&
    (store.auth.getPermissions('output', 'costing_sheet')?.toLowerCase() === 'edit' && !!project?.adminUsers?.find((id) => current.id));

  const tabs: TabList = {
    costingSheet: {
      kind: "worksheet",
      id: "costingSheet",
      title: "Costing Sheet",
      type: "sheet-output",
      fileType: "costing_sheet",
      uploadable: false,
      frozenColumns: frozenCosting,
      setFrozenColumns: setFrozenCosting,
      csvFiles:
        sheets.get("costing_sheet")?.map((sheet) => [
          {
            id: `costingSheet-${sheet.id}`,
            sheet,
            editable: costingSheetEditable ?? true,
            insertable: false,
            downloadable: true,
            updatable: havePermissions('costing_sheet'),
            rowIdentifier: "costing_option_id",
            hideColumns: () => [
              "costing_option_id",
              "formulation_id",
              "plant_split_link",
              "concatenate_formulation",
              "concatenate_manufacturing",
              "concatenate_logistics",
            ],
            headerMapping: mappingCostSheet,
            schema: (key, costingSheetEditable) =>
              schemaCostingSheet(key, costingSheetEditable, frozenCosting),
          },
        ]) || [],
      marginalCsvFiles:
        sheets.get("marginal_cost")?.map((sheet) => [
          {
            id: `marginalCostingSheet-${sheet.id}`,
            sheet,
            downloadable: true,
            editable: false,
            updatable: havePermissions('costing_sheet'),
            rowIdentifier: "costing_option_id",
            hideColumns: () => [
              "costing_option_id",
              "formulation_id",
              "concatenate_formulation",
              "concatenate_manufacturing",
              "concatenate_logistics",
            ],
            headerMapping: mappingCostSheet,
            schema: (key, costingSheetEditable) =>
              schemaCostingSheet(key, costingSheetEditable, frozenCosting),
          },
        ]) || [],
      renderExtraToolbar: havePermissions('costing_sheet') ? (worksheet) => (
        <GenerateOutputsButton onClick={worksheet.csvLoader} />
      ) : undefined,

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.outputSheets.list(project.id, undefined, undefined, {
          file_type__in: ["costing_sheet", "marginal_cost"],
        });
      },
    },
    formulationsCpl: {
      kind: "custom",
      id: "formulationsCpl",
      title: "Formulations (CPL)",
      type: "sheet-output",
      render: FormulationsCpl,
    },
    formulationSelections: {
      kind: "custom",
      id: "formulationSelections",
      title: "Formulation Selections",
      type: "sheet-output",
      render: FormulationSelectionsTab,
    },
    sourcingSelections: {
      kind: "custom",
      id: "sourcingSelections",
      title: "Sourcing Selections",
      type: "sheet-output",
      render: SourcingSelectionsTab,
    },
    rawMaterialSummary: {
      kind: "worksheet",
      id: "rawMaterialSummary",
      title: "Raw Material Summary",
      type: "sheet-output",
      fileType: "raw_material_summary",
      uploadable: false,
      csvFiles:
        sheets.get("raw_material_summary")?.map((sheet) => [
          {
            id: `rawMaterialSummary-${sheet.id}`,
            sheet,
            downloadable: true,
            editable: false,
            rowIdentifier: "raw_material_id",
            hideColumns: () => ["raw_material_id"],
            headerMapping: mappingRawMaterialSummary,
            schema: schemaRawMaterialSummary,
          },
        ]) || [],
      marginalCsvFiles: [],
      renderExtraToolbar: havePermissions('rm_summary') ? (worksheet) => (
        <GenerateOutputsButton onClick={worksheet.csvLoader} />
      ) : undefined,

      async csvLoader() {
        if (!project) {
          return;
        }
        await store.outputSheets.list(project.id, undefined, undefined, {
          file_type: "raw_material_summary",
        });
      },
    },
  };

  const filteredTabsPerRole = useMemo(() => {
    const permissions: RolesPermission[] = current
      ? current.rbacRole.permissions
      : [];
    let subSectionsAvailable = permissions
      ?.filter((item) => {
        return item.section === "output";
      })
      .map(
        (item) =>
          outputSectionTabKeys[
            item.subsection as keyof typeof outputSectionTabKeys
          ]
      );
    subSectionsAvailable = Object.keys(tabs).filter(
      (key) => subSectionsAvailable && subSectionsAvailable.includes(key)
    );
    let tabValue: TabList = {};
    subSectionsAvailable?.forEach((item: string | number) => {
      tabValue = {
        ...tabValue,
        [item]: tabs[item],
      } as TabList;
      return;
    });
    return tabValue;
  }, [tabs, current]);

  if (!project) {
    return <Loading full />;
  }

  return <WorkArea tabs={filteredTabsPerRole} />;
};

export default observer(OutputSection);
